import {
  ApiFailure,
  EntityTags,
  ErrorCode,
  failureFromResponse,
  failureFromThrown,
  isLegalWall,
  WRITE_SET_HEADER,
  type EntityTag,
  type LegalStaleDoc,
} from '@signalvote/shared';
import { API_HEADERS } from './headers';

export { isLegalWall, type LegalStaleDoc };

/** The old name of the one failure type, kept while callers still import it. Not a second class. */
export { ApiFailure as ApiError };

/**
 * The legal wall's failure: an `ApiFailure` whose reason is `legal-wall`. Any such failure is an
 * instance, whichever door parsed it, so `instanceof` keeps working for the callers that use it.
 */
export class LegalAcceptanceRequiredError extends ApiFailure {
  constructor(stale: LegalStaleDoc[], message?: string) {
    const body = { code: ErrorCode.legal_acceptance_required, message, stale };
    super(409, ErrorCode.legal_acceptance_required, message ?? 'Legal acceptance required', null, body, { kind: 'legal-wall', stale });
    this.name = 'LegalAcceptanceRequiredError';
  }

  static [Symbol.hasInstance](value: unknown): boolean {
    return value instanceof ApiFailure && value.reason.kind === 'legal-wall';
  }
}

const KNOWN_TAGS: ReadonlySet<string> = new Set(EntityTags);

/**
 * The entity types a response says its request wrote (`X-SV-Writes`), or null when it names no
 * write set, such as a read. A tag this build doesn't know is left out: no read here can name it,
 * so there is nothing of it to refresh.
 */
export function writesOf(response: Response): EntityTag[] | null {
  const header = response.headers.get(WRITE_SET_HEADER);
  if (header === null) return null;
  return header.split(',').map((tag) => tag.trim()).filter((tag): tag is EntityTag => KNOWN_TAGS.has(tag));
}

export interface ApiClientConfig {
  baseURL: string;
  getAuthToken?: () => string | null;
  /**
   * Renews the session after a 401 and resolves whether it worked. On true the request is replayed
   * once, reading the new token from `getAuthToken`; on false, a throw, or a replay that 401s again,
   * `onAuthError` fires. Requests that 401 together share one renewal.
   */
  refreshAuth?: () => Promise<boolean>;
  onAuthError?: () => void;
  onPaymentRequired?: (subscriptionStatus?: string) => void;
  /**
   * Fires when the API returns 409 legal_acceptance_required. The consumer typically
   * navigates the user to a "please accept the updated terms" screen. Distinct from
   * 409 LegalVersionMismatch (which the POST /accept handler shows as a toast).
   */
  onLegalAcceptanceRequired?: (stale: LegalStaleDoc[]) => void;
  /**
   * Fires on every response that names the entity types its request wrote (`X-SV-Writes`, on every
   * non-GET response, a refusal included), before the body is read or a refusal thrown. One cache
   * invalidates the reads of those types from here, so no call site lists keys.
   */
  onWrites?: (writes: EntityTag[]) => void;
  // Multi-tenancy header providers
  getSubsidiaryCode?: () => string | null;
  getCampaignId?: () => string | null;
}

export class ApiClient {
  private config!: ApiClientConfig;
  private isInitialized = false;
  private refreshing: Promise<boolean> | null = null;

  initialize(config: ApiClientConfig) {
    this.config = config;
    this.isInitialized = true;
  }

  getBaseURL(): string {
    if (!this.isInitialized) {
      throw new Error('ApiClient not initialized. Call initialize() first.');
    }
    return this.config.baseURL;
  }

  /**
   * Who is calling (the bearer token) and on whose behalf (subsidiary + campaign) — the headers
   * that identify a request, as opposed to the ones describing its body. Public because a few
   * payloads are fetched by something other than this client — React Native's `<Image>` loads an
   * authenticated image itself — and those must send the same headers rather than re-deriving them.
   *
   * A header whose value is missing is omitted entirely: the API reads presence, so an empty
   * `X-Campaign-Id` is a 400 rather than the honest "no campaign selected yet".
   */
  identityHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const token = this.config?.getAuthToken?.();
    const subsidiaryCode = this.config?.getSubsidiaryCode?.();
    const campaignId = this.config?.getCampaignId?.();

    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (subsidiaryCode) headers[API_HEADERS.SUBSIDIARY] = subsidiaryCode;
    if (campaignId) headers[API_HEADERS.CAMPAIGN_ID] = campaignId;

    return headers;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await this.fetchOk(endpoint, options);
    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }
    return await response.json();
  }

  /**
   * Calls an action whose success body is a file (PDF, CSV, zip). Resolves to the response so the caller reads the
   * bytes with `blob()` and the filename from `Content-Disposition`; a failure throws exactly as `request` does.
   */
  async file(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', endpoint: string, body?: unknown): Promise<Response> {
    return this.fetchOk(endpoint, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  }

  /**
   * Opens an action that sends server-sent events (`text/event-stream`). Resolves to the response once the headers
   * arrive, so the caller reads `body` as events land; a refusal throws exactly as `request` does. The same fetch as
   * every call, so auth and tenant headers ride along, which `EventSource` can't send.
   */
  async stream(endpoint: string): Promise<Response> {
    return this.fetchOk(endpoint, { method: 'GET' });
  }

  private async fetchOk(endpoint: string, options: RequestInit): Promise<Response> {
    const response = await this.fetchAuthed(endpoint, options);
    if (response.ok) return response;
    if (response.status === 401) throw new ApiFailure(401, null, 'Authentication required');

    const body: unknown = await response.json().catch(() => ({}));
    const failure = failureFromResponse(response, body);
    if (failure.reason.kind === 'payment-required') this.config.onPaymentRequired?.(failure.reason.subscriptionStatus ?? undefined);
    if (failure.reason.kind === 'legal-wall') {
      this.config.onLegalAcceptanceRequired?.(failure.reason.stale);
      throw new LegalAcceptanceRequiredError(failure.reason.stale, failure.message);
    }
    throw failure;
  }

  // Return raw response for cases where caller needs headers/status
  async requestRaw(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const response = await this.fetchAuthed(endpoint, options);

    if (response.status === 402) {
      this.config.onPaymentRequired?.();
    }

    if (response.status === 409) {
      // Sniff the body for legal_acceptance_required without consuming it — clone so
      // the caller still gets a readable response.
      try {
        const cloned = response.clone();
        const body = await cloned.json().catch(() => ({ }));
        if (isLegalWall(body)) {
          this.config.onLegalAcceptanceRequired?.(body.stale);
        }
      } catch { /* body already consumed or not JSON — ignore */ }
    }

    return response;
  }

  /** Sends the request; a 401 renews the session and replays it once. A 401 that survives that has already fired `onAuthError`. */
  private async fetchAuthed(endpoint: string, options: RequestInit): Promise<Response> {
    if (!this.isInitialized) {
      throw new Error('ApiClient not initialized. Call initialize() first.');
    }

    let response = await this.send(endpoint, options);
    if (response.status === 401 && this.config.refreshAuth && (await this.refreshOnce(this.config.refreshAuth))) {
      response = await this.send(endpoint, options);
    }
    if (response.status === 401) {
      this.config.onAuthError?.();
    }
    const writes = writesOf(response);
    if (writes !== null) this.config.onWrites?.(writes);
    return response;
  }

  private send(endpoint: string, options: RequestInit): Promise<Response> {
    // Don't set Content-Type for FormData
    const isFormData = options.body instanceof FormData;
    return fetch(`${this.config.baseURL}${endpoint}`, {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
        ...this.identityHeaders(),
      },
    }).catch((error: unknown) => {
      throw failureFromThrown(error) ?? error;
    });
  }

  private refreshOnce(refreshAuth: () => Promise<boolean>): Promise<boolean> {
    this.refreshing ??= refreshAuth()
      .catch(() => false)
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  // Helper methods for common HTTP methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, init?: Pick<RequestInit, 'signal'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...init,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  }
}
